'use client';

import { useRouter } from '@bprogress/next/app';
import { useSelector } from '@xstate/store/react';
import { useTranslations } from 'next-intl';
import { useRef } from 'react';

import {
  GTMEcommerceItem,
  GTMItemListName,
  GTMViewItemList,
  sendGTMEventEcommerce
} from '@/fo/api/gtm';
import { ProductStockStatus } from '@/fo/api/magento';
import {
  selectCountProductInCart,
  selectCountProductInWishlist,
  storeProduct,
  cartActions,
  wishlistActions,
  selectWishlistItemId,
  selectCartItemLoading
} from '@/fo/store/product';
import { UiProductTileBase } from '@/fo/ui/product/tile/base';
import { utilEventAuthModal } from '@/fo/util/event-auth-modal';
import { utilEventConfigurableProduct } from '@/fo/util/event-configurable-product';
import { utilEventProductSubscribeModal } from '@/fo/util/event-product-subscribe-modal';
import { utilGetHighBonusLabel } from '@/fo/util/get-high-bonus-label';
import { Option, MappedProduct } from '@/fo/util/map-product-data';

export interface FeatureProductTileBaseProps {
  isAuthorized: boolean;
  data: MappedProduct;
  isSelected?: boolean;
  onSelect?: (selected: boolean) => void;
  className?: string;
  isCatalog?: boolean;
  maxOptionsVisible?: number;
  maxOptionsVisibleMobile?: number;
  loyaltyLevel?: number;
  enableMicrodata?: boolean;
  listName: GTMItemListName;
  positionIndex?: number;
}

export function FeatureProductTileBase({
  isAuthorized,
  data,
  isSelected = false,
  onSelect,
  className,
  isCatalog,
  maxOptionsVisible = 3,
  maxOptionsVisibleMobile = 2,
  loyaltyLevel,
  enableMicrodata = false,
  listName,
  positionIndex = 0
}: FeatureProductTileBaseProps) {
  const ref = useRef<HTMLElement>(null);
  const router = useRouter();
  const t = useTranslations();

  const wishlistSku = data.parentSku || data?.sku;

  const cartCount = useSelector(
    storeProduct,
    selectCountProductInCart(data?.sku)
  );
  const cartLoading = useSelector(
    storeProduct,
    selectCartItemLoading(data?.sku)
  );
  const wishlistCount = useSelector(
    storeProduct,
    selectCountProductInWishlist(wishlistSku)
  );
  const wishlistItemId = useSelector(
    storeProduct,
    selectWishlistItemId(wishlistSku)
  );

  const isInWishlist = wishlistCount > 0;
  const isInCart = cartCount > 0;
  const isConfigurable = Number(data?.configurableVariants?.length) > 1;
  const isCareProgram = Boolean(
    data && 'groupType' in data && data.groupType === 1
  );

  const configVariantsFinalPrices = data?.configurableVariants
    ?.map(
      (variant) =>
        variant.product?.finalPrice ?? variant.product?.basePrice ?? 0
    )
    .filter(Boolean);

  const configVariantsBasePrices = data?.configurableVariants
    ?.map(
      (variant) =>
        variant.product?.basePrice ?? variant.product?.finalPrice ?? 0
    )
    .filter(Boolean);

  const maxPrice =
    configVariantsBasePrices && configVariantsBasePrices.length > 0
      ? Math.max(...configVariantsBasePrices)
      : data?.basePrice;

  const minPrice =
    configVariantsFinalPrices && configVariantsFinalPrices.length > 0
      ? Math.min(...configVariantsFinalPrices)
      : data?.finalPrice;

  const brand = data.labelsBrand[0];

  const onCartClick = () => {
    if (isInCart) {
      router.push('/cart');
    } else {
      if (isCareProgram && data?.items?.length) {
        cartActions.addProductsToCart(
          data?.items.map((item) => ({
            sku: item.sku,
            quantity: 1,
            product: item
          }))
        );
      } else if (isConfigurable) {
        onOptionClick(data?.configurableVariants?.[0]);
      } else {
        cartActions.addProductsToCart([
          {
            sku: data.sku,
            quantity: 1,
            product: data
          }
        ]);
      }
    }
  };

  const onWishlistClick = () => {
    if (isAuthorized) {
      if (isInWishlist && wishlistItemId) {
        wishlistActions.removeWishlistItems([
          { sku: wishlistSku, wishlistItemId }
        ]);
      } else {
        sendGTMEventEcommerce({
          event: 'add_to_wishlist',
          ecommerce: {
            value: itemGtmData.price,
            items: [{ ...itemGtmData, quantity: 1 }]
          }
        });
        wishlistActions.addProductToWishlist(data.sku, 1, data.parentSku);
      }
    } else {
      utilEventAuthModal.open('wishlist-button');
    }
  };

  const onOptionClick = (option?: Option) => {
    if (!option || !isConfigurable) return;

    utilEventConfigurableProduct.open(data);
  };

  const onSubscribeClick = () => {
    utilEventProductSubscribeModal.open(data.mindboxId);
  };

  const itemGtmData: GTMEcommerceItem = {
    item_id: data?.configurableVariants?.[0]?.sku ?? data.sku,
    item_name: data.name,
    price: String(data.specialPrice ?? data.finalPrice ?? data.basePrice),
    item_brand: brand?.name ?? '',
    item_category: data.series || data.brand || '',
    variant: data?.configurableVariants?.[0]?.label ?? data.volume ?? '',
    item_list_name: listName,
    position: positionIndex + 1
  };

  const onClick = () => {
    sendGTMEventEcommerce({
      event: 'select_item',
      ecommerce: {
        items: [itemGtmData]
      }
    });
  };

  return (
    <>
      <GTMViewItemList
        key={`gtm-${data.sku}`}
        refItem={ref}
        item={itemGtmData}
      />
      <UiProductTileBase
        ref={ref}
        key={`ui-${data.sku}`}
        stockStatus={data.stockStatus}
        mindboxId={data.mindboxId}
        reviewCount={data.reviewCount}
        bestRating={data.bestRating}
        worstRating={data.worstRating}
        isAuthorized={isAuthorized}
        link={data.link}
        image={data.thumbnail?.src || ''}
        labels={data.labels}
        title={data.name}
        series={data.series}
        description={data.subtitle}
        price={data.finalPrice ?? data.basePrice}
        oldPrice={data.finalPrice ? data.basePrice : null}
        rating={data.rating}
        bonuses={data.bonusPoints}
        bonusesMax={data.bonusPointsMax}
        bonusesLabel={
          [
            ...data.labelsBonus,
            ...(utilGetHighBonusLabel(
              data.earnedBonusPoints,
              data.finalPrice ?? data.basePrice,
              loyaltyLevel,
              isAuthorized
            )
              ? [{ name: t('Increased cashback'), color: '' }]
              : [])
          ][0]?.name
        }
        brand={{
          color: brand?.color,
          name: brand?.name,
          url: brand?.url
        }}
        isInWishlist={isInWishlist}
        isInCart={isInCart}
        options={data.configurableVariants}
        volume={data.volume}
        onClick={onClick}
        onCartClick={onCartClick}
        onWishlistClick={onWishlistClick}
        onOptionClick={onOptionClick}
        onSubscribeClick={onSubscribeClick}
        isSelected={isSelected}
        onSelect={onSelect}
        className={className}
        loadingCartButton={cartLoading}
        isCatalog={isCatalog}
        structureImage={data.structureImage || ''}
        maxOptionsVisible={maxOptionsVisible}
        maxOptionsVisibleMobile={maxOptionsVisibleMobile}
        enableMicrodata={enableMicrodata}
        lowPrice={minPrice}
        highPrice={
          data.stockStatus === ProductStockStatus.InStock ? maxPrice : null
        }
      />
    </>
  );
}
